import { toast } from "react-toastify";
import { updateStateData } from "../Store/actions";
import store from "../Store/store";
import { sendApiRequest } from "./requestConfig";
import {
  buildDefaultAccessFilters,
  seedEmbeddedFilters,
} from "./dashboardFilterSeed";

/**
 * Persist Brand Report popup selections (stores / weeks / analysis groups).
 */
export const saveBrandFilters = async (campaign_id) => {
  const userStateData = store.getState().userSelections;
  const saveFilters = await sendApiRequest(
    "filter/brand-sharing/save",
    {
      ...userStateData.brand_filters,
      campaign: campaign_id,
    },
    "POST"
  );

  if (saveFilters.success === true) {
    toast("Report filters saved successfully", { type: "success" });
    store.dispatch(
      updateStateData({
        report_filter_saved: true,
      })
    );
    return { success: true };
  }

  toast("Sorry, something went wrong", { type: "error" });
  return { success: false };
};

/**
 * Full-catalog defaults for Retailer view (and for Brand when nothing was saved yet).
 * Includes tab-scoped keys derived from each section's filter list.
 */
const defaultDashboardFilter = () => buildDefaultAccessFilters();

/**
 * Build brand_filters + retailer_filters from the brand-sharing API response.
 *
 * - brand_filters: what the Brand Report popup shows / what Brand view is constrained by
 * - retailer_filters: always full campaign catalogs for Retailer / Cyreen view
 */
const buildAccessFilterBags = (apiResponse) => {
  const hasSavedFilters =
    typeof apiResponse.impression_filter !== "undefined" ||
    typeof apiResponse.performance_filter !== "undefined";

  // Nothing saved yet → Brand and Retailer start from the same catalog defaults.
  if (!hasSavedFilters) {
    const defaults = defaultDashboardFilter();
    return {
      brand_filters: { ...apiResponse, ...defaults },
      retailer_filters: { ...apiResponse, ...defaults },
    };
  }

  // Saved Brand Report exists. Retailer view still uses full catalogs.
  // Keep legacy single_performance_analysis_group payloads as-is when present.
  const brandFilters =
    typeof apiResponse.single_performance_analysis_group !== "undefined"
      ? apiResponse
      : {
          ...apiResponse,
          performance_filter: {
            ...apiResponse.performance_filter,
            // Keep analysis_group as the Brand Report source of truth;
            // tab-scoped keys are rebuilt when we seed embedded_* bags.
          },
        };

  return {
    brand_filters: brandFilters,
    retailer_filters: defaultDashboardFilter(),
  };
};

/**
 * Fetch Brand Report constraints, then seed the live dashboard filter bags.
 *
 * Call this only after catalogs + report settings are in Redux so seeding
 * can resolve stores, adlevels, weeks, regions, and section filter lists.
 *
 * @param {string|number} campaignid
 * @param {string} viewMode - "Brand" uses brand_filters; otherwise retailer_filters
 */
export const fetchBrandFilters = async (campaignid, viewMode) => {
  // Filters.jsx waits on this flag so the UI does not render half-seeded values.
  store.dispatch(
    updateStateData({
      brand_filters_loading: true,
      loading: true,
    })
  );

  const fetchFilters = await sendApiRequest(
    "filter/brand-sharing/" + campaignid,
    {},
    "GET"
  );

  if (fetchFilters.success !== true) {
    toast("Sorry, something went wrong", { type: "error" });
    store.dispatch(
      updateStateData({
        brand_filters_loading: false,
        loading: false,
      })
    );
    return { success: false };
  }

  const accessBags = buildAccessFilterBags(fetchFilters.response);

  // Which bag drives the live dashboard for this page load?
  const selectionKeyword =
    viewMode === "Brand" ? "brand_filters" : "retailer_filters";
  const accessSource = accessBags[selectionKeyword];

  // Seed tab-scoped embedded_* state from the chosen access source.
  const embeddedBags = seedEmbeddedFilters(accessSource);

  store.dispatch(
    updateStateData({
      ...accessBags,
      ...embeddedBags,
      brand_filters_loading: false,
      loading: false,
    })
  );

  return { success: true };
};
